import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { familyFestEventPageFieldAllowsLineBreaks, normalizeFamilyFestEventPageValue, validateFamilyFestEventPageValue } from '@application/helpers/family-fest-event-page-field-helper';
import { Box, Typography, useTheme } from '@mui/material';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
// Placeholder prompt by what the field is on the page (heading, body text, button label or link).
const placeholderKeyFor = (field) => {
    if (field.type === 'Url')
        return 'buttonLink';
    if (field.type === 'Heading')
        return 'heading';
    if (field.key.endsWith('.label'))
        return 'buttonLabel';
    return 'bodyText';
};
const EventPageTextInput = ({ control, name, field, defaultValue, label }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const value = useWatch({ control, name: name }) ?? '';
    // The design default is always valid on the page (e.g. a relative PDF link), so only admin
    // changes are checked.
    const isDefault = normalizeFamilyFestEventPageValue(value) === normalizeFamilyFestEventPageValue(defaultValue);
    const error = isDefault ? null : validateFamilyFestEventPageValue(field, value);
    const errorMessage = error
        ? t(`events.eventDetails.eventPageTab.errors.${error.key}`, { limit: error.limit })
        : undefined;
    const multiline = familyFestEventPageFieldAllowsLineBreaks(field);
    const length = normalizeFamilyFestEventPageValue(value).length;
    const nearLimit = length >= field.maxLength * 0.9;
    // The input stops typing at maxLength, so say why instead of silently ignoring keystrokes.
    const limitReached = length >= field.maxLength;
    // Shown like a validation error, but it doesn't block saving: the value is still valid.
    const limitMessage = limitReached
        ? t('events.eventDetails.eventPageTab.maxCharactersReached', { limit: field.maxLength })
        : undefined;
    const fieldHint = length === 0 ? { text: t('events.eventDetails.eventPageTab.hiddenOnPage'), color: theme.palette.text.secondary } : null;
    return (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%', children: [_jsx(ControlledFilledTextField, { control: control, name: name, bg: 'grey8', label: label, placeholder: t(`events.eventDetails.eventPageTab.placeholders.${placeholderKeyFor(field)}`), multiline: multiline, rows: multiline ? (field.maxLength > 200 ? 5 : 3) : undefined, errorMessage: errorMessage ?? limitMessage, trimWhitespace: false, 
                // The theme paints filled-input placeholders in the value colour; dim it so it reads as a hint.
                styles: { '& .MuiFilledInput-input::placeholder': { color: theme.palette.text.disabled, opacity: 1 } }, slotProps: { htmlInput: { maxLength: field.maxLength } } }), (fieldHint || field.type !== 'Url') && (_jsxs(Box, { display: 'flex', justifyContent: 'space-between', gap: 1, children: [_jsx(Typography, { variant: 'caption', color: fieldHint?.color, children: fieldHint?.text ?? '' }), field.type !== 'Url' && (_jsx(Typography, { variant: 'caption', color: limitReached
                            ? theme.palette.error.main
                            : nearLimit
                                ? theme.palette.warning.dark
                                : theme.palette.text.secondary, sx: { fontVariantNumeric: 'tabular-nums', flexShrink: 0 }, children: t('events.eventDetails.eventPageTab.characterCount', { count: length, max: field.maxLength }) }))] }))] }));
};
export default EventPageTextInput;
